/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Page Personal Account component
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Misha Kokoulin
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.0.0
 */
import { Fragment, memo, useCallback, useEffect, useMemo } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import {
    fastActionSwitchCheckbox as fastActionSwitchCheckboxAction,
    setBasketContext,
    setIsFastActionPageAction,
    setIsFastBasketPageAction,
} from '@/main/components/Table/actions/tableActions';
import { EFastBasketSwitchboxContext, EFastWizardType } from '@/main/components/Table/constants';
import { makeSelectorTableFastActionAllIn } from '@/main/components/Table/selectors/tableSelectors';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { useTable } from '@components/Table/hooks/useTable';
import { ITableField } from '@components/Table/reducers/tableReducer';
import { ISwitchCheckboxPayload, TableFilterPresetQueryId, TableNameType } from '@components/Table/types/tableTypes';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationPagePersonalAccount,
} from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalOpenPayload } from '@modules/modal/types/modalActionTypes';
import { makeSelectIsDensity } from '@modules/storage/storageSelectors';
import { IRootReducer } from '@reducers/rootReducer';

import { Table } from '../../components/common';
import Title from '../../components/common/Title/Title';
import TableColumnUtil from '../../components/Table/TableColumnUtil';
import withAsyncStateManagement from '../../modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '../../modules/internationalization/components/PageLocale';
import { ICommonTranslation, IPagePersonalAccountTranslation } from '../../modules/internationalization/types';
import { personalAccountColumnNames, PersonalAccountField } from '../../types/personalAccountTypes';
import { makeSelectFeatureEnabled } from '../PageAuth/selectors/authSelectors';
import { wizardBillsDocSetParamsAction } from '../WizardBillsDoc/wizardBillsDocActions';
import {
    cellConfig,
    getColumnCellType,
    getColumnFilterType,
    getColumnFixedType,
} from './components/PersonalAccountTable/Columns/Cells';
import PersonalAccountTableFooter from './components/PersonalAccountTable/PersonalAccountTableFooter';
import PersonalAccountTitle from './components/PersonalAccountTitle/PersonalAccountTitle';
import { pagePersonalAccountSaga } from './pagePersonalAccountSaga';

interface IPagePersonalAccountProps {
    isFastActionPage?: boolean;
    isFastBasketPage?: boolean;
    basketContext?: EFastBasketSwitchboxContext;
    fastWizard?: EFastWizardType;
}


export const PagePersonalAccount = memo(({
                                             isDensity,
                                             openModal,
                                             translationCommon,
                                             translationPagePersonalAccount,
                                             isFastActionPage,
                                             isFastBasketPage,
                                             fastWizard,
                                             basketContext,
                                         }: IDispatchProps & IConnectedProps & IPagePersonalAccountProps) => {

    const location = useLocation();
    const dispatch = useDispatch();

    const fastActionAllIn = useSelector(makeSelectorTableFastActionAllIn());
    const isMainOperationsEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.MAIN_OPERATIONS));

    const isWizardFastBillsDoc = fastWizard === EFastWizardType.WIZARD_BILLS_DOCS;
    const isWizardFastReportDoc = fastWizard === EFastWizardType.WIZARD_REPORT_DOCS;
    const isFastWizard = isWizardFastBillsDoc || isWizardFastReportDoc;
    const disableCheckedAllMessage = isFastBasketPage
        ? translationCommon.corporateTableFooter.types.account?.INQUIRY_CREATE?.tooltip
        : '';

    const {
        basketAllIn,
        componentsFeature,
        count,
        data,
        fields,
        isFilterSelected,
        isLoading,
        isTableResizeEnabled,
        sort,
        tableCheckedItems,

        dropFilterAndSort,
        loadTableList,
        saveResizedChange,
        sendTableFields,
        setTableFilterPreset,
        setTableSort,
        switchCheckbox,
    } = useTable(TableNameType.account);

    const tableColumnUtil = useMemo(() =>
        new TableColumnUtil<PersonalAccountField, IPagePersonalAccountTranslation & ICommonTranslation>({
            cellConfig,
            componentsFeature,
            fields: fields as Array<ITableField<PersonalAccountField>>,
            isTableResizeEnabled,
            getColumnCellType,
            getColumnFilterType,
            getColumnFixedType,
            columnNames: personalAccountColumnNames,
            translation: { ...translationCommon, ...translationPagePersonalAccount },
        }), [componentsFeature, fields, isTableResizeEnabled, translationCommon, translationPagePersonalAccount]);

    const fastActionSwitchCheckbox = useCallback((payload: ISwitchCheckboxPayload) => {
        dispatch(fastActionSwitchCheckboxAction(payload));
    }, [dispatch]);

    useEffect(() => {
            // триггерим setParams чтобы сделать валидацию кнопки "Далее"
            if (isFastWizard && isMainOperationsEnabled) {
                dispatch(wizardBillsDocSetParamsAction({}));
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        },[tableCheckedItems]);

    useEffect(() => {
        dispatch(setIsFastActionPageAction(isFastActionPage));
        dispatch(setIsFastBasketPageAction(isFastBasketPage));
        dispatch(setBasketContext(basketContext));

        const filterId = new URLSearchParams(location.search).get(TableFilterPresetQueryId);
        setTableFilterPreset({ filterId, tableName: TableNameType.account });

        // очищаем фильтры при входе на страницу
        if (isMainOperationsEnabled && isFastActionPage) {
            dropFilterAndSort({ skipLoadList: true });
        }
        return () => {
            // при выходе со страницы очищаем фильтры
            if (isMainOperationsEnabled && isFastActionPage) {
                dropFilterAndSort({ skipLoadList: true });
            }
        }
    },[
        basketContext,
        dispatch,
        dropFilterAndSort,
        isFastActionPage,
        isFastBasketPage,
        isMainOperationsEnabled,
        setTableFilterPreset,
        location.search,
    ]);

    return (
        <Fragment>
            <PageLocale lazy fileNames={[
                TranslationFileNamesEnum.common,
                TranslationFileNamesEnum.corporate,
                TranslationFileNamesEnum.pagePersonalAccount,
                TranslationFileNamesEnum.pageAccountInfo,
            ]}/>

            {
                isWizardFastBillsDoc &&
                <Typography isDangerousInnerHTML variant="body1">
                    {translationPagePersonalAccount.title.wizardFastBillsDocSubtitle}
                </Typography>
            }
            {
                isWizardFastReportDoc &&
                <Typography isDangerousInnerHTML variant="body1">
                    {translationPagePersonalAccount.title.wizardFastReportDocSubstitle}
                </Typography>
            }

            <Title pageName={ECommonTranslationPages.personalAccount}/>

            <PersonalAccountTitle
                counter={count}
                isFilterSelected={isFilterSelected()}
                openModal={openModal}
                sendTableFields={fields => sendTableFields({ fields, tableName: TableNameType.account })}
                tableFields={fields as Array<ITableField<PersonalAccountField>>}
                translation={translationPagePersonalAccount}
                translationCommon={translationCommon}
                onDropFiltersClick={() => dropFilterAndSort({})}
            />
            <Table
                basketAllIn={isFastBasketPage ? fastActionAllIn : basketAllIn}
                columns={tableColumnUtil.customizeTableColumns()}
                counter={count}
                data={data}
                disableCheckedAll={!data.length || isFastBasketPage}
                disableCheckedAllMessage={disableCheckedAllMessage}
                fields={fields}
                footer={<PersonalAccountTableFooter/>}
                getProps={() => ({ className: 'table-with-checkboxes' })}
                isDensity={isDensity}
                isLoading={isLoading}
                loadTableList={loadTableList}
                rowDataName="number"
                saveResizedChange={saveResizedChange}
                setTableSort={setTableSort}
                showCheckedColumn={true}
                sort={sort}
                switchCheckbox={isFastBasketPage ? fastActionSwitchCheckbox : switchCheckbox}
                tableCheckedItems={tableCheckedItems}
                tableDataName="personalAccount"
                tableName={TableNameType.account}
            />
        </Fragment>
    );
});

interface IConnectedProps {
    isDensity: boolean;
    translationCommon: ICommonTranslation;
    translationPagePersonalAccount: IPagePersonalAccountTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isDensity: makeSelectIsDensity(),
    translationCommon: makeSelectTranslationCommon(),
    translationPagePersonalAccount: makeSelectTranslationPagePersonalAccount(),
});

export interface IDispatchProps {
    openModal: (payload: IModalOpenPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    openModal: (payload: IModalOpenPayload) => dispatch(modalOpen(payload)),
});

export default withAsyncStateManagement(
    connect(mapStateToProps, mapDispatchToProps)(PagePersonalAccount),
    { sagas: pagePersonalAccountSaga },
);
